import { ApolloQueryResult } from 'apollo-client';
import { merge } from 'lodash';
import { concat, difference, map, sortBy } from 'lodash';

import {
    DealsCriteriaKey,
    getDealFilterCriteria
} from '../declarations/filter/filter';
import { getNumberValue } from '../service/FormatHelpers/FormatHelpers';
import { DropdownChooserItem } from '../typings/common';
import {
    Deal,
    DealDetail,
    DealDetailCampaign,
    DealDetailJava,
    DealsSourceParams,
    dealStatus,
    dealStatusForFilter,
    InfoByCid
} from '../typings/deals';
import {
    DealsQueryFilter,
    FilterExpression,
    FilterPartialExpression
} from '../typings/filter';
import {
    CompleteReasonInput,
    DealRowFragment,
    DealsQuery,
    DealTypeInput,
    StatusDirect,
    StatusDirectInput
} from '../typings/graphql-grid';

import {
    filterArrayByNullAndUndefined,
    getValueFromApiFormat,
    mapSortParamsToString
} from './common/common';
import {
    FilterCriteriaMappings,
    getStrictExpressions,
    textToDealFilterExpression
} from './filter/filter';

const mappings = new FilterCriteriaMappings<DealsQueryFilter>();
const { toEnum, toNumber } = mappings.valueMappings;

const statusAndCompleteReasonMapper = ({
    operator,
    value
}: FilterExpression): {
    statusContains: StatusDirectInput[];
    completeReasonContains?: CompleteReasonInput[];
} => {
    const mappedValue = toEnum(dealStatusForFilter)(value);
    const statusContains: StatusDirectInput[] = [];
    const completeReasonContains: CompleteReasonInput[] = [];

    mappedValue.forEach(item => {
        switch (item) {
            case dealStatusForFilter.ACTIVE:
                statusContains.push(StatusDirectInput.ACTIVE);
                break;
            case dealStatusForFilter.RECEIVED:
                statusContains.push(StatusDirectInput.RECEIVED);
                break;
            case dealStatusForFilter.ARCHIVED:
                statusContains.push(StatusDirectInput.ARCHIVED);
                break;
            case dealStatusForFilter.COMPLETED_BY_CLIENT:
                if (!statusContains.includes(StatusDirectInput.COMPLETED)) {
                    statusContains.push(StatusDirectInput.COMPLETED);
                }
                completeReasonContains.push(CompleteReasonInput.BY_CLIENT);
                break;
            case dealStatusForFilter.COMPLETED_BY_DATE:
                if (!statusContains.includes(StatusDirectInput.COMPLETED)) {
                    statusContains.push(StatusDirectInput.COMPLETED);
                }
                completeReasonContains.push(CompleteReasonInput.BY_DATE);
                break;
            case dealStatusForFilter.COMPLETED_BY_PUBLISHER:
                if (!statusContains.includes(StatusDirectInput.COMPLETED)) {
                    statusContains.push(StatusDirectInput.COMPLETED);
                }
                completeReasonContains.push(CompleteReasonInput.BY_PUBLISHER);
                break;
            default:
        }
    });

    return {
        statusContains,
        ...(completeReasonContains.length ? { completeReasonContains } : null)
    };
};

const dealCriteriaMappings: {
    [key in DealsCriteriaKey]: (
        expression: FilterExpression
    ) => DealsQueryFilter
} = {
    id: mappings.includes('dealIdIncluded', 'dealIdExcluded'),
    type: mappings.equality('typeIn', toEnum(DealTypeInput)),
    name: mappings.contains('nameContains', 'nameNotContains'),
    status: statusAndCompleteReasonMapper,
    shows: mappings.comparison('minShows', 'maxShows', toNumber, false),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber, false),
    cpc: mappings.comparison('minCpc', 'maxCpc', toNumber, false),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber, false),
    cpm: mappings.comparison('minCpm', 'maxCpm', toNumber, false),
    minPrice: mappings.comparison(
        'minMinPrice',
        'maxMinPrice',
        toNumber,
        false
    ),
    spent: mappings.comparison('minSpent', 'maxSpent', toNumber, false),
    numberOfLinkedCampaigns: mappings.comparison(
        'minNumberOfLinkedCampaigns',
        'maxNumberOfLinkedCampaigns',
        toNumber,
        false
    )
};

const expressionToDealsQueryFilter = (
    expression: FilterExpression
): DealsQueryFilter => {
    return dealCriteriaMappings[expression.criteriaKey](expression);
};

export const mapDealFilterToApiFormat = (
    expressions: FilterPartialExpression[]
): DealsQueryFilter => {
    const criteria = getDealFilterCriteria();

    return getStrictExpressions(
        expressions,
        criteria,
        textToDealFilterExpression
    )
        .map(expressionToDealsQueryFilter)
        .reduce(merge, {});
};

export const mapSourceParamsToDealsQueryVariables = (
    params: DealsSourceParams
): {
    login: string;
    orderBy?: { order: string; field: string }[];
    filter: DealsQueryFilter | undefined;
    needStats: boolean;
} => {
    const { login, sortParams, filter, noStats } = params;

    return {
        login,
        orderBy: sortParams && mapSortParamsToString(sortParams),
        filter: filter ? mapDealFilterToApiFormat(filter) : undefined,
        needStats: !noStats
    };
};

const mapRowToDeal = (row: DealRowFragment): Deal => {
    return {
        id: row.id,
        name: row.name || '',
        status: mapDealRowStatusToDealStaus(row.status),
        completeReason: getValueFromApiFormat(row.completeReason),
        dateStart: getValueFromApiFormat(row.dateStart),
        dateEnd: getValueFromApiFormat(row.dateEnd),
        description: getValueFromApiFormat(row.description),
        expectedMoneyPerWeek: getNumberValue(row.expectedMoneyPerWeek),
        expectedImpressionsPerWeek: getNumberValue(
            row.expectedImpressionsPerWeek
        ),
        dealType: row.dealType,
        contacts: getValueFromApiFormat(row.contacts),
        minPrice: getNumberValue(row.minPrice),
        targetingsText: row.targetingsText || '',
        agencyFeePercent: getNumberValue(row.agencyFeePercent),
        currencyCode: getValueFromApiFormat(row.currencyCode),
        publisherName: row.publisherName || '',
        numberOfLinkedCampaigns: row.numberOfLinkedCampaigns || 0,
        clicks: getNumberValue(row.clicks),
        cpc: getNumberValue(row.cpc),
        cpm: getNumberValue(row.cpm),
        ctr: getNumberValue(row.ctr),
        placements: (row.placements || []).map(placement => ({
            domain: placement.domain || '',
            formats: filterArrayByNullAndUndefined(placement.formats || [])
        })),
        shows: getNumberValue(row.shows),
        spent: getNumberValue(row.spent)
    };
};

export const createMenuItems = (
    campaigns: string[],
    items: DropdownChooserItem[],
    infoByCid: InfoByCid
): DropdownChooserItem[] => {
    const missingCampaignIds: string[] = difference(
        campaigns,
        map(items, 'id')
    );
    const missingCampaign = missingCampaignIds.map(cid => ({
        id: cid,
        name: infoByCid[cid].name,
        addition: cid,
        isDisabled: true
    }));

    const resultCampaigns = concat(items, missingCampaign);

    return sortBy(resultCampaigns, ['name']);
};

export const getClientCampaing = (
    campaignsByClient: {
        [key: string]: DealDetailCampaign[];
    },
    chosenClient: string
): string[] => {
    return campaignsByClient[chosenClient].map(({ campaignId }) =>
        String(campaignId)
    );
};

export const getClientsCamps = (
    items: DropdownChooserItem[],
    employedClients: string[]
): DropdownChooserItem[] => {
    return items.map(({ id, name }) => ({
        id,
        name,
        isDisabled: employedClients.includes(name)
    }));
};

export const mapDealsQueryResultToEntitiesQueryResponse = (
    result: ApolloQueryResult<DealsQuery>
): {
    data: Array<Deal>;
    total: number;
    allItemsIds: Array<string>;
    cacheKey: string;
} => {
    const dealsResult = result.data.client.deals;

    return {
        data: dealsResult.rowset.map(mapRowToDeal),
        total: dealsResult.totalCount,
        allItemsIds: dealsResult.dealIds.map(dealId => String(dealId)),
        cacheKey: dealsResult.cacheKey
    };
};

function mapDealRowStatusToDealStaus(status: StatusDirect): dealStatus {
    const dictionary = {
        [StatusDirect.RECEIVED]: dealStatus.RECEIVED,
        [StatusDirect.ACTIVE]: dealStatus.ACTIVE,
        [StatusDirect.COMPLETED]: dealStatus.COMPLETED,
        [StatusDirect.ARCHIVED]: dealStatus.ARCHIVED
    };

    if (!dictionary[status]) {
        throw new Error(`Статус сделки ${status} не поддерживается`);
    }

    return dictionary[status];
}

export const mapDealDetailDataToDeal = (
    dealDetail: DealDetailJava
): DealDetail => {
    const { deal, campaigns } = dealDetail;

    return {
        campaigns: campaigns.sort(),
        deal: {
            name: deal.name,
            id: String(deal.id),
            clicks: deal.clicks,
            contacts: deal.contacts,
            cpc: deal.cpc,
            cpm: deal.cpm,
            ctr: deal.ctr,
            description: deal.description,
            placements: deal.placements,
            shows: deal.shows,
            spent: deal.spent,
            status: deal.status,
            agencyFeePercent: deal.agency_fee_percent,
            completeReason: deal.complete_reason,
            currencyCode: deal.currency_code,
            dateEnd: deal.date_end,
            dateStart: deal.date_start,
            dealType: deal.deal_type,
            expectedImpressionsPerWeek: deal.expected_impressions_per_week,
            expectedMoneyPerWeek: deal.expected_money_per_week,
            minPrice: deal.min_price,
            numberOfLinkedCampaigns: campaigns.length,
            publisherName: deal.publisher_name,
            targetingsText: deal.targetings_text
        }
    };
};
